import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

interface AcademicSessionFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialValues?: AcademicSessionFormValues & { id?: string };
}

interface AcademicSessionFormValues {
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const AcademicSessionForm: React.FC<AcademicSessionFormProps> = ({
  open,
  onClose,
  onSuccess,
  initialValues,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const isEdit = !!initialValues?.id;

  const formik = useFormik<AcademicSessionFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      startDate: initialValues?.startDate
        ? new Date(initialValues.startDate).toISOString().split("T")[0]
        : "",
      endDate: initialValues?.endDate
        ? new Date(initialValues.endDate).toISOString().split("T")[0]
        : "",
      isActive: initialValues?.isActive || false,
    },
    enableReinitialize: true,

    validationSchema: Yup.object({
      name: Yup.string().required("Session name is required"),
      startDate: Yup.date().required("Start date is required"),
      endDate: Yup.date()
        .required("End date is required")
        .min(Yup.ref("startDate"), "End Date must be after Start Date")
        .test(
          "session-duration",
          "Session duration must be between 10 and 14 months",
          function (value) {
            const startDate = this.parent.startDate;
            if (!startDate || !value) return true;
            const start = new Date(startDate);
            const end = new Date(value);
            const diffMs = end.getTime() - start.getTime();
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            const diffMonthsApprox = diffDays / 30.4375;
            return diffMonthsApprox >= 10 && diffMonthsApprox <= 14;
          },
        ),
      isActive: Yup.boolean(),
    }),

    onSubmit: async (values) => {
      setLoading(true);
      setSubmitError("");
      try {
        if (isEdit) {
          await axios.patch(
            `/system-settings/academic-sessions/${initialValues?.id}`,
            values,
          );
        } else {
          await axios.post("/system-settings/academic-sessions", values);
        }
        onSuccess();
      } catch (error: any) {
        const apiMessage = error?.response?.data?.message;
        const message = Array.isArray(apiMessage)
          ? apiMessage.join(", ")
          : apiMessage || "Failed to save academic session";
        setSubmitError(message);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Academic Session" : "Create New Academic Session"}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          {submitError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          ) : null}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="name"
                label="Session Name (e.g., 2025-26)"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                name="startDate"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={formik.values.startDate}
                onChange={formik.handleChange}
                error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                helperText={formik.touched.startDate && formik.errors.startDate}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                name="endDate"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={formik.values.endDate}
                onChange={formik.handleChange}
                error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                helperText={formik.touched.endDate && formik.errors.endDate}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                    name="isActive"
                  />
                }
                label="Set as Active Session (Deactivates others)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSubmitError("");
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Session" : "Create Session"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AcademicSessionForm;
